/**
 * Activity timeline — every audit event in the company, with filters
 * by user, action prefix, and a refresh button. Backed by the same
 * /v1/companies/:id/audit endpoint that the dashboard's strip uses, just
 * with a higher default limit.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { ActivityFeed } from "../components/ActivityFeed";
import { dashboardApi } from "../api/queries/companies";
import { useUiStore } from "../stores/ui";

const ACTION_GROUPS: Array<{ label: string; value: string }> = [
  { label: "All", value: "" },
  { label: "Users", value: "user." },
  { label: "Admin", value: "admin." },
  { label: "Devices", value: "device." },
  { label: "Agent hosts", value: "agent_host." },
];

export function ActivityPage() {
  const companyId = useUiStore((s) => s.activeCompanyId);
  const [actorFilter, setActorFilter] = useState<string>("");
  const [groupFilter, setGroupFilter] = useState<string>("");

  const members = useQuery({
    queryKey: ["members", companyId],
    queryFn: () => dashboardApi.members(companyId!),
    enabled: !!companyId,
  });

  if (!companyId) {
    return (
      <main className="mx-auto max-w-[1100px] px-6 py-6">
        <div className="card p-6 text-secondary">No company selected.</div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[1100px] px-6 py-6">
      <header className="mb-4 flex items-baseline justify-between">
        <h1 className="text-[20px] font-semibold tracking-[-0.01em]">Activity</h1>
        <span className="text-[12px] text-tertiary">
          Live feed · refreshes every 10s
        </span>
      </header>

      <div className="card p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1" role="tablist" aria-label="Event group">
          {ACTION_GROUPS.map((g) => (
            <button
              key={g.value}
              role="tab"
              aria-selected={groupFilter === g.value}
              type="button"
              className={`btn btn--sm ${
                groupFilter === g.value ? "btn--secondary" : "btn--ghost"
              }`}
              onClick={() => setGroupFilter(g.value)}
            >
              {g.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <label className="field-inline">
            <span className="text-[11px] text-tertiary mr-1">Actor</span>
            <select
              className="input input--sm"
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
            >
              <option value="">Everyone</option>
              {(members.data?.members ?? []).map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.display_name} ({m.email})
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="iconbtn"
            onClick={() => window.location.reload()}
            title="Hard refresh"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="card p-2">
        <ActivityFeed
          companyId={companyId}
          limit={200}
          actionFilter={groupFilter || undefined}
          actorFilter={actorFilter || undefined}
          rowsExpandable
          emptyMessage="No events match the current filter."
        />
      </div>
    </main>
  );
}
